export const FILES = "abcdefgh";
export const RANKS = "12345678";

export type Square = string;

export type BoardKeyState = {
  focused: Square;
  selected: Square | null;
  buffer: string;
};

export type BoardKeyResult = {
  state: BoardKeyState;
  action?: { type: "move"; from: Square; to: Square } | { type: "announce"; text: string };
};

const SQUARE_RE = /^[a-h][1-8]$/;

export function isSquare(value: string): value is Square {
  return SQUARE_RE.test(value);
}

export function shiftSquare(square: Square, df: number, dr: number): Square | null {
  if (!isSquare(square)) return null;
  const file = FILES.indexOf(square[0]);
  const rank = RANKS.indexOf(square[1]);
  const nextFile = file + df;
  const nextRank = rank + dr;
  if (nextFile < 0 || nextFile > 7 || nextRank < 0 || nextRank > 7) return null;
  return `${FILES[nextFile]}${RANKS[nextRank]}`;
}

export function initialBoardKeyState(start: Square = "e2"): BoardKeyState {
  return { focused: start, selected: null, buffer: "" };
}

export function applyBoardKey(
  state: BoardKeyState,
  key: string,
  orientation: "w" | "b" = "w"
): BoardKeyResult {
  const direction = orientation === "b" ? -1 : 1;
  const lower = key.length === 1 ? key.toLowerCase() : key;

  if (lower === "Escape") {
    return {
      state: { ...state, selected: null, buffer: "" },
      action: { type: "announce", text: "Selection cleared." },
    };
  }

  if (lower === "ArrowLeft") {
    const next = shiftSquare(state.focused, -direction, 0) || state.focused;
    return {
      state: { ...state, focused: next, buffer: "" },
      action: { type: "announce", text: next },
    };
  }
  if (lower === "ArrowRight") {
    const next = shiftSquare(state.focused, direction, 0) || state.focused;
    return {
      state: { ...state, focused: next, buffer: "" },
      action: { type: "announce", text: next },
    };
  }
  if (lower === "ArrowUp") {
    const next = shiftSquare(state.focused, 0, direction) || state.focused;
    return {
      state: { ...state, focused: next, buffer: "" },
      action: { type: "announce", text: next },
    };
  }
  if (lower === "ArrowDown") {
    const next = shiftSquare(state.focused, 0, -direction) || state.focused;
    return {
      state: { ...state, focused: next, buffer: "" },
      action: { type: "announce", text: next },
    };
  }

  if (lower === "Enter" || lower === " ") {
    if (!state.selected) {
      return {
        state: { ...state, selected: state.focused, buffer: "" },
        action: { type: "announce", text: `Selected ${state.focused}` },
      };
    }
    if (state.selected === state.focused) {
      return {
        state: { ...state, selected: null, buffer: "" },
        action: { type: "announce", text: "Selection cleared." },
      };
    }
    return {
      state: { ...state, selected: null, buffer: "" },
      action: { type: "move", from: state.selected, to: state.focused },
    };
  }

  if (/^[a-h]$/.test(lower)) {
    return { state: { ...state, buffer: lower } };
  }
  if (/^[1-8]$/.test(lower) && state.buffer.length === 1) {
    const square = `${state.buffer}${lower}`;
    if (!isSquare(square)) return { state: { ...state, buffer: "" } };
    if (!state.selected) {
      return {
        state: { focused: square, selected: square, buffer: "" },
        action: { type: "announce", text: `Selected ${square}` },
      };
    }
    return {
      state: { focused: square, selected: null, buffer: "" },
      action: { type: "move", from: state.selected, to: square },
    };
  }

  return { state };
}
